import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Calendar Events — time-anchored commitments with optional recurrence.
 *
 * An Event is DISTINCT from a scheduled Task (a task is work to close; an event
 * is a block of time that happens) and from a Reminder (a fire-once nudge).
 * Recurrence is stored as a small, deterministic rule and expanded lazily into
 * occurrences for whatever window the UI asks about — never materialised as
 * thousands of rows. Everything is UTC and pure so it is trivially testable.
 */

export type RecurrenceFreq = 'none' | 'daily' | 'weekly' | 'monthly';
export const RECURRENCE_FREQS: readonly RecurrenceFreq[] = ['none', 'daily', 'weekly', 'monthly'] as const;

export interface Recurrence {
  readonly freq: RecurrenceFreq;
  /** Repeat every N units of `freq` (>= 1). Ignored when freq is 'none'. */
  readonly interval: number;
  /** Inclusive ISO instant after which the series stops, or null for open-ended. */
  readonly until: string | null;
}

export const NO_RECURRENCE: Recurrence = { freq: 'none', interval: 1, until: null };

export type EventStatus = 'confirmed' | 'tentative' | 'cancelled';

export interface CalendarEvent extends RecordEnvelope {
  readonly kind: 'event';
  readonly title: string;
  /** ISO instant the (first) occurrence starts. */
  readonly startsAt: string;
  readonly durationMinutes: number;
  readonly location: string;
  readonly recurrence: Recurrence;
  readonly status: EventStatus;
  /** Linked tasks/projects/people (ids only, no duplication). */
  readonly linkedIds: readonly EntityId[];
}

export interface EventInput {
  readonly title: string;
  readonly startsAt: string;
  readonly durationMinutes?: number;
  readonly location?: string;
  readonly recurrence?: Recurrence;
  readonly privacyClass?: PrivacyClass;
}

export interface EventPatch {
  readonly title?: string;
  readonly startsAt?: string;
  readonly durationMinutes?: number;
  readonly location?: string;
  readonly recurrence?: Recurrence;
}

/** A single concrete instance of an event within a queried window. */
export interface EventOccurrence {
  readonly eventId: EntityId;
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
}

/** Advance an ISO instant by one recurrence step (UTC, calendar-aware for months). */
export function advanceByRecurrence(iso: string, freq: RecurrenceFreq, interval: number): string {
  const d = new Date(iso);
  const step = Math.max(1, interval);
  if (freq === 'daily') d.setUTCDate(d.getUTCDate() + step);
  else if (freq === 'weekly') d.setUTCDate(d.getUTCDate() + 7 * step);
  else if (freq === 'monthly') d.setUTCMonth(d.getUTCMonth() + step);
  return d.toISOString();
}

const MAX_OCCURRENCES = 1000;

/**
 * Expand an event into occurrences overlapping [rangeStartIso, rangeEndIso].
 * Pure and bounded. Cancelled events expand to nothing. A non-recurring event
 * yields at most one occurrence.
 */
export function expandOccurrences(
  event: Pick<CalendarEvent, 'id' | 'title' | 'startsAt' | 'durationMinutes' | 'recurrence' | 'status'>,
  rangeStartIso: string,
  rangeEndIso: string,
): EventOccurrence[] {
  const out: EventOccurrence[] = [];
  if (event.status === 'cancelled') return out;

  const rangeStart = Date.parse(rangeStartIso);
  const rangeEnd = Date.parse(rangeEndIso);
  if (Number.isNaN(rangeStart) || Number.isNaN(rangeEnd) || rangeEnd < rangeStart) return out;

  const durMs = Math.max(0, event.durationMinutes) * 60_000;
  const untilMs = event.recurrence.until ? Date.parse(event.recurrence.until) : Number.POSITIVE_INFINITY;
  const { freq, interval } = event.recurrence;

  let cursor = event.startsAt;
  for (let i = 0; i < MAX_OCCURRENCES; i++) {
    const startMs = Date.parse(cursor);
    if (Number.isNaN(startMs)) break;
    if (startMs > rangeEnd || startMs > untilMs) break;
    const endMs = startMs + durMs;
    // Overlap test: occurrence intersects the queried window.
    if (endMs >= rangeStart && startMs <= rangeEnd) {
      out.push({
        eventId: event.id,
        title: event.title,
        startsAt: new Date(startMs).toISOString(),
        endsAt: new Date(endMs).toISOString(),
      });
    }
    if (freq === 'none') break;
    cursor = advanceByRecurrence(cursor, freq, interval);
  }
  return out;
}

/** The UTC day window [start, end] for an ISO date (YYYY-MM-DD or full ISO). */
export function dayWindow(dateIso: string): { startIso: string; endIso: string } {
  const day = dateIso.slice(0, 10);
  return { startIso: `${day}T00:00:00.000Z`, endIso: `${day}T23:59:59.999Z` };
}

export interface OccurrenceConflict {
  readonly a: EventOccurrence;
  readonly b: EventOccurrence;
}

/**
 * Find overlapping occurrence pairs so the calendar can surface conflicts
 * instead of silently double-booking. Pure; O(n log n + k) over a sorted list.
 */
export function findConflicts(occurrences: readonly EventOccurrence[]): OccurrenceConflict[] {
  const sorted = [...occurrences].sort((x, y) => x.startsAt.localeCompare(y.startsAt));
  const out: OccurrenceConflict[] = [];
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      // Sorted by start, so once b starts at/after a ends, no later b overlaps a.
      if (sorted[j]!.startsAt < sorted[i]!.endsAt) {
        out.push({ a: sorted[i]!, b: sorted[j]! });
      } else {
        break;
      }
    }
  }
  return out;
}
